import React, { createContext, useContext, useEffect, useState } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (password: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const existingToken = sessionStorage.getItem('pkb_web_token');
    if (existingToken) {
      setToken(existingToken);
      setIsAuthenticated(true);
    }
  }, []);

  const login = (password: string): boolean => {
    // Get password from env (set via VITE_ prefix for vite)
    // In production, this would be an actual API call
    const correctPassword = import.meta.env.VITE_PKB_WEB_PASSWORD || 'changeme';

    if (password === correctPassword) {
      // Generate a simple token (timestamp-based for this test)
      const token = `web-${Date.now()}`;
      sessionStorage.setItem('pkb_web_token', token);
      setToken(token);
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    sessionStorage.removeItem('pkb_web_token');
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return null; // Will be handled by router redirect
  }

  return <>{children}</>;
};
